import React, { useState } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import SearchModal from "./SearchModal";
import { Video } from "../types";

const HeaderContainer = styled.header`
    background: white;
    color: #333;
    padding: 1rem 0;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Nav = styled.nav`
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 20px;
`;

const LogoContainer = styled(Link)`
    display: flex;
    align-items: center;
    text-decoration: none;
`;

const LogoImg = styled.img`
    height: 40px;
    width: auto;

    @media (max-width: 768px) {
        height: 32px;
    }
`;

const DesktopNav = styled.div`
    display: flex;
    align-items: center;
    gap: 2rem;

    @media (max-width: 768px) {
        display: none;
    }
`;

const NavLink = styled(Link).withConfig({
    shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>`
    color: ${({ active }) => (active ? "#667eea" : "#333")};
    text-decoration: none;
    font-weight: 500;
    padding: 0.5rem 0;

    &:hover {
        color: #667eea;
    }
`;

const AdminButton = styled(Link)`
    background: #667eea;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    text-decoration: none;
    font-weight: 500;

    &:hover {
        background: #5a6fd8;
    }
`;

const MobileMenuButton = styled.button`
    display: none;
    background: none;
    border: none;
    color: #333;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 768px) {
        display: flex;
    }
`;

const SearchButton = styled.button`
    background: none;
    border: none;
    color: #666;
    cursor: pointer;
    padding: 0.5rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.5rem;
    transition: color 0.3s ease;

    &:hover {
        color: #333;
    }

    @media (max-width: 768px) {
        margin-right: 0.3rem;
        margin-top: 2px;
    }
`;

const MobileButtons = styled.div`
    display: none;
    align-items: center;

    @media (max-width: 768px) {
        display: flex;
    }
`;

const MobileMenu = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>`
    display: none;

    @media (max-width: 768px) {
        display: ${({ isOpen }) => (isOpen ? "block" : "none")};
        position: fixed;
        top: 68px;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 99;
    }
`;

const MobileMenuContent = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>`
    @media (max-width: 768px) {
        background: white;
        padding: 1rem;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        transform: ${({ isOpen }) => (isOpen ? "translateY(0)" : "translateY(-100%)")};
        transition: transform 0.3s ease;
        margin-top: 0;
    }
`;

const MobileNavLink = styled(Link)`
    display: block;
    color: #333;
    text-decoration: none;
    padding: 1rem 0;
    border-bottom: 1px solid #eee;
    text-align: center;
    font-size: 1.1rem;

    &:last-child {
        border-bottom: none;
    }
`;

interface Content {
    id: number;
    title: string;
    description: string;
    content_type: string;
    category: string;
    youtube_url: string;
    thumbnail_url: string;
    custom_description: string;
}

interface HeaderProps {
    videos?: Video[];
    onVideoSelect?: (video: Video) => void;
    onContentSelect?: (content: Content) => void;
}

const Header: React.FC<HeaderProps> = ({ videos = [], onVideoSelect, onContentSelect }) => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
    const location = useLocation();

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const toggleSearch = () => {
        setIsSearchOpen(!isSearchOpen);
    };

    const handleVideoSelect = (video: Video) => {
        if (onVideoSelect) {
            onVideoSelect(video);
        }
    };

    const handleContentSelect = (content: Content) => {
        if (onContentSelect) {
            onContentSelect(content);
        }
    };

    return (
        <HeaderContainer>
            <Nav>
                <LogoContainer to="/">
                    <LogoImg src="/logo.png" alt="Client Operation Server" />
                </LogoContainer>

                <DesktopNav>
                    <NavLink to="/" active={location.pathname === "/"}>
                        Home
                    </NavLink>
                    <NavLink to="/works" active={location.pathname === "/works"}>
                        Works
                    </NavLink>
                    <NavLink to="/contacts" active={location.pathname === "/contacts"}>
                        Contact
                    </NavLink>
                    {/* Product and PR links hidden from navigation */}
                    {/*
                    <NavLink to="/product" active={location.pathname === "/product"}>
                        Product
                    </NavLink>
                    <NavLink to="/pr" active={location.pathname === "/pr"}>
                        PR
                    </NavLink>
                    */}
                    <SearchButton onClick={toggleSearch}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.35-4.35"></path>
                        </svg>
                    </SearchButton>
                    <AdminButton to="/admin">Admin</AdminButton>
                </DesktopNav>

                <MobileButtons>
                    <SearchButton onClick={toggleSearch}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.35-4.35"></path>
                        </svg>
                    </SearchButton>
                    <MobileMenuButton onClick={toggleMenu}>{isMenuOpen ? "✕" : "☰"}</MobileMenuButton>
                </MobileButtons>
            </Nav>

            <MobileMenu isOpen={isMenuOpen} onClick={() => setIsMenuOpen(false)}>
                <MobileMenuContent isOpen={isMenuOpen} onClick={(e) => e.stopPropagation()}>
                    <MobileNavLink to="/" onClick={() => setIsMenuOpen(false)}>
                        Home
                    </MobileNavLink>
                    <MobileNavLink to="/works" onClick={() => setIsMenuOpen(false)}>
                        Works
                    </MobileNavLink>
                    <MobileNavLink to="/contacts" onClick={() => setIsMenuOpen(false)}>
                        Contacts
                    </MobileNavLink>
                    {/* Product and PR links hidden from mobile navigation */}
                    {/*
                    <MobileNavLink to="/product" onClick={() => setIsMenuOpen(false)}>
                        Product
                    </MobileNavLink>
                        PR
                    </MobileNavLink>
                    */}
                    <MobileNavLink to="/admin" onClick={() => setIsMenuOpen(false)}>
                        Admin
                    </MobileNavLink>
                </MobileMenuContent>
            </MobileMenu>

            <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} videos={videos} onVideoSelect={handleContentSelect} />
        </HeaderContainer>
    );
};

export default Header;
